import React, { useEffect, useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
} from '@mui/material';

function blankForm() {
  return {
    code: '',
    kind: 'percent',
    value: 10,
    currency: 'USD',
    min_subtotal: 0,
    max_uses: 0,
    max_uses_per_customer: 0,
    starts_at: '',
    ends_at: '',
    status: 'active',
    description: '',
  };
}

function toInputDate(v) {
  if (!v) return '';
  try {
    return new Date(v).toISOString().slice(0, 10);
  } catch {
    return '';
  }
}

/**
 * Create / edit a discount code. Percent discounts are shown in % to
 * feel natural; we convert to/from basis points (100 = 1%) on the wire
 * since the backend stores integer bps.
 */
export default function DiscountDialog({ open, onClose, onSubmit, isSubmitting, initial }) {
  const [form, setForm] = useState(blankForm());

  useEffect(() => {
    if (open) {
      if (initial) {
        setForm({
          ...blankForm(),
          ...initial,
          starts_at: toInputDate(initial.starts_at),
          ends_at: toInputDate(initial.ends_at),
        });
      } else {
        setForm(blankForm());
      }
    }
  }, [open, initial]);

  const set = (k, v) => setForm((prev) => ({ ...prev, [k]: v }));
  const canSubmit = form.code.trim().length > 0 && Number(form.value) > 0 && !isSubmitting;

  const valueInUnit = form.kind === 'percent'
    ? Number(form.value || 0) / 100
    : Number(form.value || 0);

  const handleValueChange = (v) => {
    const num = Number(v);
    if (form.kind === 'percent') {
      set('value', Math.round(num * 100));
    } else {
      set('value', num);
    }
  };

  const submit = () => {
    onSubmit({
      ...form,
      code: form.code.trim().toUpperCase(),
      value: Number(form.value),
      min_subtotal: Number(form.min_subtotal || 0),
      max_uses: Number(form.max_uses || 0),
      max_uses_per_customer: Number(form.max_uses_per_customer || 0),
      starts_at: form.starts_at || null,
      ends_at: form.ends_at || null,
    });
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth='sm'
    >
      <DialogTitle>{initial ? 'Edit discount' : 'New discount'}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing='14px' sx={{ pt: '4px' }}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing='12px'>
            <TextField
              fullWidth size='small' required
              label='Code'
              value={form.code}
              onChange={(e) => set('code', e.target.value.toUpperCase())}
              placeholder='SUMMER20'
            />
            <FormControl size='small' sx={{ minWidth: 160 }}>
              <InputLabel>Status</InputLabel>
              <Select label='Status' value={form.status} onChange={(e) => set('status', e.target.value)}>
                <MenuItem value='active'>Active</MenuItem>
                <MenuItem value='paused'>Paused</MenuItem>
                <MenuItem value='archived'>Archived</MenuItem>
              </Select>
            </FormControl>
          </Stack>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing='12px'>
            <FormControl size='small' sx={{ minWidth: 160 }}>
              <InputLabel>Type</InputLabel>
              <Select label='Type' value={form.kind} onChange={(e) => set('kind', e.target.value)}>
                <MenuItem value='percent'>Percent off</MenuItem>
                <MenuItem value='fixed'>Fixed amount</MenuItem>
              </Select>
            </FormControl>
            <TextField
              fullWidth size='small'
              type='number'
              inputProps={{ step: '0.01' }}
              label={form.kind === 'percent' ? 'Discount (%)' : 'Amount off'}
              value={valueInUnit}
              onChange={(e) => handleValueChange(e.target.value)}
            />
            <TextField
              size='small'
              label='Currency'
              value={form.currency}
              onChange={(e) => set('currency', e.target.value.toUpperCase())}
              inputProps={{ maxLength: 3 }}
              sx={{ maxWidth: 130 }}
            />
          </Stack>
          <TextField
            fullWidth size='small' type='number'
            inputProps={{ step: '0.01' }}
            label='Minimum order subtotal'
            value={form.min_subtotal}
            onChange={(e) => set('min_subtotal', e.target.value)}
          />
          <Stack direction={{ xs: 'column', md: 'row' }} spacing='12px'>
            <TextField
              fullWidth size='small' type='number'
              label='Max uses (0 = unlimited)'
              value={form.max_uses}
              onChange={(e) => set('max_uses', e.target.value)}
            />
            <TextField
              fullWidth size='small' type='number'
              label='Per-customer limit (0 = unlimited)'
              value={form.max_uses_per_customer}
              onChange={(e) => set('max_uses_per_customer', e.target.value)}
            />
          </Stack>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing='12px'>
            <TextField
              fullWidth size='small' type='date'
              label='Starts'
              InputLabelProps={{ shrink: true }}
              value={form.starts_at}
              onChange={(e) => set('starts_at', e.target.value)}
            />
            <TextField
              fullWidth size='small' type='date'
              label='Ends'
              InputLabelProps={{ shrink: true }}
              value={form.ends_at}
              onChange={(e) => set('ends_at', e.target.value)}
            />
          </Stack>
          <TextField
            fullWidth size='small' multiline minRows={2}
            label='Description (internal)'
            value={form.description}
            onChange={(e) => set('description', e.target.value)}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant='contained' onClick={submit} disabled={!canSubmit}>
          {isSubmitting ? 'Saving…' : (initial ? 'Save' : 'Create')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
